import { NextPage } from 'next'
import dynamic from 'next/dynamic'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import React from 'react'

import { appName, appUrl } from '@/config/env'
import { getFloatingMenu } from '@/services/apps'
import { getDetailNews, getNews } from '@/services/news'
import { wrapper } from '@/store'
import { useAppSelector } from '@/utils/hooks'

const PressReleaseSlug = dynamic(() => import('./PressReleaseSlug'), { ssr: false })
const Page: NextPage<{ detailReady: boolean }> = ({ detailReady }) => {
  const router = useRouter()
  const { t } = useTranslation()
  const { data: detailNews } = useAppSelector(
    getDetailNews.select({
      params: { slug: String(router.query?.slug) },
      headers: { 'Accept-Language': String(router.locale) },
    })
  )
  const news = detailNews?.data
  const title = `${t('press:title')} - ${news?.title ?? ''}`
  const url = `${appUrl}${router.locale === router.defaultLocale ? '' : `/${router.locale}`}/media/press/${
    router.query?.slug
  }`

  return (
    <>
      {news ? (
        <Head>
          <title>{`${title} - ${appName}`}</title>
          <meta
            name='description'
            content={news.shortDescription}
          />
          <link
            rel='canonical'
            href={url}
          />
          <meta
            property='og:type'
            content='article'
          />
          <meta
            property='og:title'
            content={title}
          />
          <meta
            property='og:description'
            content={news.shortDescription}
          />
          <meta
            property='og:url'
            content={url}
          />
          <meta
            property='og:image'
            content={news.imageFiles?.desktop?.url ?? ''}
          />
          <meta
            name='twitter:card'
            content='summary_large_image'
          />
        </Head>
      ) : null}
      <main className='min-h-screen bg-gray-100'>
        <PressReleaseSlug detailReady={detailReady} />
      </main>
    </>
  )
}

export const getProps = wrapper.getServerSideProps((store) => async ({ locale, params }) => {
  const paramsList = {
    params: { skip: 0, limit: 4, keyword: { skipSlug: String(params?.slug) } },
    headers: { 'Accept-Language': String(locale) },
  }
  const detail = await store.dispatch(
    getDetailNews.initiate({ params: { slug: String(params?.slug) }, headers: { ...paramsList.headers } })
  )
  await Promise.all([
    store.dispatch(getNews.initiate(paramsList)),
    store.dispatch(getFloatingMenu.initiate({ headers: { ...paramsList.headers } })),
  ])
  return {
    props: {
      detailReady: detail && detail.isSuccess,
      ...(await serverSideTranslations(String(locale), ['common', 'press'])),
    },
  }
})

export default Page
